import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase_func/firebase';
import Notebar from '../components/Notebar';
import CalendarWidget from './Calendar';
import SuggestionsWidget from './Suggestions';
import WikiWidget from './Wiki';
import ProjectWidget from './Project';
import NotesHistoryWidget from './NotesHistoryWidget';
import Settings from './Settings';
import '../styles/NotebookHome.css';
import notesService from "../firebase_func/subCollections/notes";


const NotebookHome = () => {
  const [notes, setNotes] = useState([]);
  const [activeWidget, setActiveWidget] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentDate, setCurrentDate] = useState('2024-11-25');
  const [selectedNotes, setSelectedNotes] = useState([]);

  const navigate = useNavigate();
  const user = auth.currentUser;

  const toggleWidget = (widget) => {
    if (widget === 'suggestions') {
      setShowSuggestions(!showSuggestions);
    } else {
      setActiveWidget((prev) => (prev === widget ? null : widget));
    }
  };

  const addNote = async (noteText) => {
    if (!noteText.trim() || !user) return;
  
    const noteId = crypto.randomUUID();
  
    // Optimistic UI update
    setNotes((prevNotes) => [
      ...prevNotes,
      {
        id: noteId,
        text: noteText,
        title: "Home | New Note",
        llmOutput: "Saving...",
      },
    ]);
  
    try {
      const response = await fetch("http://localhost:5000/api/generate-ai-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.uid,
          noteId,
          noteContent: noteText,
        }),
      });
  
      const data = await response.json();
  
      await notesService.addNote(
        user.uid,
        noteId,
        noteText,
        data.result,
        "Home | New Note"
      );
        
      // Update note with real LLM output
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.id === noteId ? { ...note, llmOutput: data.result } : note
        )
      );
    } catch (err) {
      console.error("Failed to save note:", err);
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.id === noteId ? { ...note, llmOutput: "❌ Error saving note" } : note
        )
      );
    }
  };
  
    

  const renderActiveWidget = () => {
    switch (activeWidget) {
      case 'calendar':
        return <CalendarWidget />;
      case 'wiki':
        return <WikiWidget />;
      case 'project':
        return <ProjectWidget />;
      case 'notes-history':
        return (
          <NotesHistoryWidget
            currentDate={currentDate}
            notes={notes}
            fetchNotesByDate={(date) => setCurrentDate(date)}
            resetToPresent={() => setCurrentDate('2024-11-25')}
          />
        );
      case 'settings':
        return <Settings />;
      default:
        return null;
    }
  };

  const getSuggestionsContent = () => {
    switch (activeWidget) {
      case 'calendar':
        return {
          title: 'Calendar Suggestions',
          content: [
            'Add important deadlines to your calendar',
            'Set up recurring meetings',
            'Review upcoming events for the week',
          ],
        };
      case 'wiki':
        return {
          title: 'Wiki Suggestions',
          content: [
            'Document your project processes',
            'Update team contact information',
            'Add links to important resources',
          ],
        };
      case 'project':
        return {
          title: 'Project Suggestions',
          content: [
            'Break down large tasks into smaller ones',
            'Set milestones for your project',
            'Assign team members to tasks',
          ],
        };
      case 'notes-history':
        return {
          title: 'Notes History Suggestions',
          content: [
            'Review past meeting notes',
            'Tag important decisions',
            'Follow up on action items',
          ],
        };
      default:
        return {
          title: 'General Suggestions',
          content: [
            'Select a widget to see specific suggestions',
            'Use widgets to organize your work',
            'Keep your notes updated regularly',
          ],
        };
    }
  };

  return (
    <div className="notebook-home-container">
      {!activeWidget && (
        <header className="notebook-header">
          <h1>Notebook Home</h1>
        </header>
      )}

      <div className="main-content">
        <div className="widgets-area">
          {activeWidget && (
            <div className="widget-container">{renderActiveWidget()}</div>
          )}

          <div className="notes-container">
            {notes.map((note) => (
              <div key={note.id} className="note">
                <div className="note-header">
                  <input
                    type="checkbox"
                    checked={selectedNotes.includes(note.id)}
                    onChange={() =>
                      setSelectedNotes((prevSelected) =>
                        prevSelected.includes(note.id)
                          ? prevSelected.filter((id) => id !== note.id)
                          : [...prevSelected, note.id]
                      )
                    }
                  />
                  <p className="note-time">
                    {note.time} | <strong>{note.title}</strong>
                  </p>
                </div>
                <p>{note.text}</p>
                <div className="llm-output">
                  <strong>LLM Output:</strong> {note.llmOutput}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showSuggestions && (
          <div className="suggestions-panel">
            <SuggestionsWidget {...getSuggestionsContent()} />
          </div>
        )}
      </div>

      <Notebar
        activeWidgets={[activeWidget, showSuggestions ? 'suggestions' : null].filter(Boolean)}
        toggleWidget={toggleWidget}
        addNote={addNote}
      />
    </div>
  );
};

export default NotebookHome;
